/**
 * Markdown-it dialect constructs that AstroMark 0.1.0 renders differently (OrbitalPlan/08 §6). Used twice: as warnings in
 * `renderPage({ format: 'astromark' })` so an author sees why a page looks different, and by the corpus diff
 * (scripts/astromark-corpus-diff.mjs) to name the cause of a structural difference.
 *
 * Detection is line based and skips fenced code and inline code spans, so a code sample that shows HTML is not reported.
 */
export type LegacyConstruct =
    | "raw-html"
    | "raw-iframe"
    | "container"
    | "toc-placeholder"
    | "underscore-emphasis"
    | "setext-heading"
    | "bare-url"
    | "angle-autolink"
    | "html-entity"
    | "table-caption"
    | "data-image"
    | "typographer";

export interface ConstructHit {
    construct: LegacyConstruct;
    /** 1-based line of the first occurrence. */
    line: number;
    /** How many lines carry it. */
    count: number;
    /** The first occurrence, trimmed to 60 characters. */
    sample: string;
}

/** What each construct does under AstroMark, phrased for an author (diagnostic message) and for the corpus report. */
export const CONSTRUCT_EFFECT: Record<LegacyConstruct, string> = {
    "raw-html": "raw HTML is shown as text (AstroMark escapes it)",
    "raw-iframe": "an <iframe> embed is shown as text; write the embed as a link to {atlas}/embed/{guid}",
    container: "`:::` containers (tabs, warning…) are not AstroMark syntax and render as paragraphs",
    "toc-placeholder": "`[[toc]]` is rendered by Ark's adapter (not by the compiler)",
    "underscore-emphasis": "`_text_` / `__text__` stay literal; use `*text*` / `**text**`",
    "setext-heading": "an underlined (setext) heading becomes a paragraph; use `#`",
    "bare-url": "a bare URL is not turned into a link; use `[text](url)`",
    "angle-autolink": "`<https://…>` is shown as text; use `[text](url)`",
    "html-entity": "HTML entities (`&amp;`, `&copy;`) are shown literally",
    "table-caption": "`Table: caption` stays a paragraph instead of a <caption>",
    "data-image": "inline `data:` images are blocked (src emptied) by AstroMark's URL policy",
    typographer: "smart quotes, dashes and (c) are not substituted (markdown-it typographer)",
};

const HTML_TAG = /<\/?([a-zA-Z][a-zA-Z0-9-]*)(\s[^<>]*)?\/?>/;
const IFRAME = /<iframe\b/i;
const CONTAINER = /^\s{0,3}:::/;
const TOC = /^(\$\{toc\}|\[\[?_?toc_?\]?\]|\$<toc(\{[^}]*\})>)$/i;
const UNDERSCORE = /(^|[^\w\\])(__?)(?=\S)(?:(?!\2).)+?\S\2(?!\w)/;
const SETEXT = /^\s{0,3}(=+|-+)\s*$/;
const LIST_OR_QUOTE_OR_FENCE = /^\s{0,3}([-*+]\s|\d+[.)]\s|>|```|~~~|\||#)/;
const BARE_URL = /(^|[\s(])(https?:\/\/[^\s<>()]+)/;
const ANGLE_AUTOLINK = /<(https?:\/\/|mailto:)[^>\s]+>/i;
const ENTITY = /&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]+);/i;
const TABLE_CAPTION = /^\s{0,3}(Table)?:\s+\S/;
const DATA_IMAGE = /(\]\(\s*|src\s*=\s*["']?)data:image\//i;
const TYPOGRAPHER = /(^|[^-])--(?!-)|\.\.\.|\((c|r|tm|p)\)|(^|[\s(])["']\S/i;
const FENCE = /^\s{0,3}(```|~~~)/;

/** Lines of `body` outside fenced code, with inline code spans blanked. */
function proseLines(body: string): { text: string; line: number }[] {
    const out: { text: string; line: number }[] = [];
    let fence: string | null = null;
    body.split(/\r?\n/).forEach((raw, index) => {
        const open = FENCE.exec(raw);
        if (fence) {
            if (open && open[1] === fence) fence = null;
            return;
        }
        if (open) {
            fence = open[1];
            return;
        }
        if (/^( {4}|\t)/.test(raw) && !/^\s*([-*+]|\d+[.)])\s/.test(raw)) return; // indented code (best effort)
        out.push({ text: raw.replace(/`[^`]*`/g, (m) => " ".repeat(m.length)), line: index + 1 });
    });
    return out;
}

/** Which legacy constructs a body uses, first occurrence first. */
export function detectLegacyConstructs(body: string | null | undefined): ConstructHit[] {
    if (!body) return [];
    const hits = new Map<LegacyConstruct, ConstructHit>();
    const hit = (construct: LegacyConstruct, line: number, sample: string) => {
        const existing = hits.get(construct);
        if (existing) existing.count++;
        else hits.set(construct, { construct, line, count: 1, sample: sample.trim().slice(0, 60) });
    };

    const lines = proseLines(body);
    lines.forEach(({ text, line }, i) => {
        const withoutAutolinks = text.replace(ANGLE_AUTOLINK, " ");
        if (IFRAME.test(text)) hit("raw-iframe", line, text);
        else if (HTML_TAG.test(withoutAutolinks)) hit("raw-html", line, text);
        if (ANGLE_AUTOLINK.test(text)) hit("angle-autolink", line, text);
        if (CONTAINER.test(text)) hit("container", line, text);
        if (TOC.test(text.trim())) hit("toc-placeholder", line, text);
        if (UNDERSCORE.test(text.replace(/\]\([^)]*\)|https?:\/\/\S+/g, " "))) hit("underscore-emphasis", line, text);
        const previous = i > 0 ? lines[i - 1] : null;
        if (
            SETEXT.test(text) &&
            previous &&
            previous.line === line - 1 &&
            previous.text.trim() !== "" &&
            !LIST_OR_QUOTE_OR_FENCE.test(previous.text) &&
            !SETEXT.test(previous.text)
        ) {
            hit("setext-heading", line, `${previous.text} / ${text}`);
        }
        const url = BARE_URL.exec(text.replace(/\]\([^)]*\)/g, " ").replace(/(src|href)\s*=\s*["'][^"']*["']/gi, " "));
        if (url) hit("bare-url", line, url[2]);
        if (ENTITY.test(text)) hit("html-entity", line, text);
        if (TABLE_CAPTION.test(text)) {
            const near = [lines[i - 2], lines[i - 1], lines[i + 1], lines[i + 2]].some((l) => l && /^\s*\|/.test(l.text));
            if (near) hit("table-caption", line, text);
        }
        if (DATA_IMAGE.test(text)) hit("data-image", line, text);
        if (!HTML_TAG.test(withoutAutolinks) && TYPOGRAPHER.test(text.replace(/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/, ""))) hit("typographer", line, text);
    });

    return Array.from(hits.values()).sort((a, b) => a.line - b.line);
}
